import type Phaser from "phaser";
import { explosionRing, spark } from "@yjsgames/phaser-fx";
import { BRICK_SHARD_TEXTURES } from "./brickDebris.js";
import { DEPTH } from "./depthLayers.js";
import type { FxImagePool } from "./fxImagePool.js";
import { PALETTE_NUM } from "../themes/palette.js";

export interface BrickImpactBounds {
  readonly projectileX: number;
  readonly projectileY: number;
  readonly brickX: number;
  readonly brickY: number;
  readonly brickWidth: number;
  readonly brickHeight: number;
}

export interface BrickImpactFxOptions extends BrickImpactBounds {
  readonly tint: number;
  readonly isCrit?: boolean;
  readonly damageRatio?: number;
  readonly rng?: () => number;
}

export function darkenTint(tint: number, factor = 0.62): number {
  const r = Math.max(0, Math.min(255, Math.round(((tint >> 16) & 255) * factor)));
  const g = Math.max(0, Math.min(255, Math.round(((tint >> 8) & 255) * factor)));
  const b = Math.max(0, Math.min(255, Math.round((tint & 255) * factor)));
  return (r << 16) | (g << 8) | b;
}

export function computeBrickImpactScale(damageRatio = 0): number {
  const ratio = Math.max(0, Math.min(1, Number.isFinite(damageRatio) ? damageRatio : 0));
  return 0.72 + Math.sqrt(ratio) * 0.98;
}

export function computeBrickImpactPoint(opts: BrickImpactBounds): { x: number; y: number } {
  const halfW = Math.max(1, opts.brickWidth / 2);
  const halfH = Math.max(1, opts.brickHeight / 2);
  const minX = opts.brickX - halfW;
  const maxX = opts.brickX + halfW;
  const minY = opts.brickY - halfH;
  const maxY = opts.brickY + halfH;

  if (
    opts.projectileX < minX ||
    opts.projectileX > maxX ||
    opts.projectileY < minY ||
    opts.projectileY > maxY
  ) {
    return {
      x: Math.min(maxX, Math.max(minX, opts.projectileX)),
      y: Math.min(maxY, Math.max(minY, opts.projectileY)),
    };
  }

  const dx = opts.projectileX - opts.brickX;
  const dy = opts.projectileY - opts.brickY;
  if (dx === 0 && dy === 0) return { x: opts.brickX, y: minY };

  const tx = dx === 0 ? Number.POSITIVE_INFINITY : halfW / Math.abs(dx);
  const ty = dy === 0 ? Number.POSITIVE_INFINITY : halfH / Math.abs(dy);
  const t = Math.min(tx, ty);
  return {
    x: opts.brickX + dx * t,
    y: opts.brickY + dy * t,
  };
}

export function computeBrickImpactNormal(opts: BrickImpactBounds): { x: number; y: number } {
  const point = computeBrickImpactPoint(opts);
  const halfW = Math.max(1, opts.brickWidth / 2);
  const halfH = Math.max(1, opts.brickHeight / 2);
  const minX = opts.brickX - halfW;
  const maxX = opts.brickX + halfW;
  const minY = opts.brickY - halfH;
  const maxY = opts.brickY + halfH;
  const eps = 0.001;

  const onLeft = Math.abs(point.x - minX) <= eps;
  const onRight = Math.abs(point.x - maxX) <= eps;
  const onTop = Math.abs(point.y - minY) <= eps;
  const onBottom = Math.abs(point.y - maxY) <= eps;
  const horizontal = onLeft ? -1 : onRight ? 1 : 0;
  const vertical = onTop ? -1 : onBottom ? 1 : 0;

  if (horizontal !== 0 && vertical !== 0) {
    const dx = Math.abs(opts.projectileX - opts.brickX) / halfW;
    const dy = Math.abs(opts.projectileY - opts.brickY) / halfH;
    return dx > dy ? { x: horizontal, y: 0 } : { x: 0, y: vertical };
  }
  if (horizontal !== 0) return { x: horizontal, y: 0 };
  if (vertical !== 0) return { x: 0, y: vertical };
  return { x: 0, y: -1 };
}

export function emitBrickImpactFx(
  scene: Phaser.Scene,
  pool: FxImagePool,
  opts: BrickImpactFxOptions,
): readonly Phaser.GameObjects.GameObject[] {
  const rng = opts.rng ?? Math.random;
  const point = computeBrickImpactPoint(opts);
  const normal = computeBrickImpactNormal(opts);
  const color = opts.isCrit ? PALETTE_NUM.damageText : darkenTint(opts.tint);
  const impactScale = computeBrickImpactScale(opts.damageRatio);
  const startX = point.x + normal.x * 2;
  const startY = point.y + normal.y * 2;
  const out: Phaser.GameObjects.GameObject[] = [];

  const shardCount = opts.isCrit ? 7 : 5;
  const tangent = { x: -normal.y, y: normal.x };
  for (let i = 0; i < shardCount; i += 1) {
    const spread = (rng() - 0.5) * (opts.isCrit ? 1.35 : 1.05);
    const dirX = normal.x + tangent.x * spread;
    const dirY = normal.y + tangent.y * spread;
    const len = Math.max(0.001, Math.hypot(dirX, dirY));
    const nx = dirX / len;
    const ny = dirY / len;
    const dist = (opts.isCrit ? 34 : 25) * impactScale * (0.65 + rng() * 0.55);
    const size = (opts.isCrit ? 1.35 : 1.05) * impactScale * (0.75 + rng() * 0.55);
    const rotation = Math.atan2(ny, nx) + (rng() - 0.5) * 0.8;
    const shard = pool
      .acquire()
      .setTexture(BRICK_SHARD_TEXTURES[i % BRICK_SHARD_TEXTURES.length]!)
      .setPosition(startX, startY)
      .setDepth(DEPTH.projectile + 0.35)
      .setTint(color)
      .setBlendMode("ADD")
      .setAlpha(opts.isCrit ? 0.88 : 0.72)
      .setScale(size)
      .setRotation(rotation)
      .setActive(true)
      .setVisible(true);
    scene.tweens.add({
      targets: shard,
      x: startX + nx * dist,
      y: startY + ny * dist + 4,
      alpha: 0,
      scale: size * 0.25,
      rotation: rotation + (rng() - 0.5) * Math.PI,
      duration: (opts.isCrit ? 310 : 250) * (0.85 + rng() * 0.35),
      ease: "Quad.easeOut",
      onComplete: () => pool.release(shard),
    });
    out.push(shard);
  }

  const ring = explosionRing(scene, startX, startY, (opts.isCrit ? 9 : 7) * impactScale, {
    color,
    alpha: opts.isCrit ? 0.48 : 0.34,
    endScale: opts.isCrit ? 2.15 : 1.9,
    duration: opts.isCrit ? 260 : 210,
    depth: DEPTH.projectile + 0.2,
  });
  const flash = spark(scene, startX, startY, "hit_spark_white", {
    startScale: (opts.isCrit ? 0.46 : 0.34) * impactScale,
    endScale: (opts.isCrit ? 1.05 : 0.82) * impactScale,
    alpha: opts.isCrit ? 0.86 : 0.74,
    duration: opts.isCrit ? 240 : 190,
    tint: color,
    depth: DEPTH.projectile + 0.3,
  });
  out.push(ring, flash);
  return out;
}
